import { inject, ref } from 'vue';
import type { Ref } from 'vue';

import type {
    Resource,
    Client,
    Company,
    User,
} from '@keyvue/one-api-client';
import type { Company as LegacyCompany, User as LegacyUser } from '@keyvue/one-api-client-legacy';

export interface ReactiveCollection<T extends Resource> {
    data: Ref<T[]>;
    count: Ref<number>;
    total: Ref<number>;
    loading: Ref<boolean>;
    error: Ref<any>;
    load: (params?: { [key: string]: any }) => Promise<void>;
}

export default function useClient() {
    const client = inject<Client>('client');

    if (!client) {
        throw new Error('api plugin not enabled');
    }

    const legacyCompany = inject<Ref<LegacyCompany>>('legacyCompany');
    const legacyUser = inject<Ref<LegacyUser>>('legacyUser');

    const getLazyReactiveCollection = <T extends Resource>(Class: new (...args: any[]) => T, params: object = {}): ReactiveCollection<T> => {
        const collectionParams = ref<object>(params);
        const collectionData = ref<T[]>([]) as Ref<T[]>;
        const collectionCount = ref<number>(0);
        const collectionTotal = ref<number>(0);
        const collectionLoading = ref<boolean>(false);
        const collectionError = ref<any>(null);

        const load = async (updatedParams?: { [key: string]: any }) => {
            collectionParams.value = updatedParams ?? collectionParams.value;
            const { data, total } = await client.getCollection<T>(Class, collectionParams.value);

            try {
                collectionError.value = null;
                collectionLoading.value = true;
                collectionData.value.splice(0);
                collectionData.value = data;
                collectionCount.value = data.length;
                collectionTotal.value = total;
                collectionLoading.value = false;
            } catch (error) {
                collectionError.value = error;
            } finally {
                collectionLoading.value = false;
            }
        };

        return {
            data: collectionData,
            count: collectionCount,
            total: collectionTotal,
            loading: collectionLoading,
            error: collectionError,
            load,
        };
    };

    const getReactiveCollection = async <T extends Resource>(Class: new (...args: any[]) => T, params: object = {}): Promise<ReactiveCollection<T>> => {
        const collection = getLazyReactiveCollection<T>(Class, params);
        await collection.load();

        return collection;
    };

    const getLazySubCollection = <T extends Resource>(parent: Resource, Class: new (...args: any[]) => T, subResourceIdentifier: string, params: object = {}): ReactiveCollection<T> => {
        const collectionParams = ref<object>(params);
        const collectionData = ref<T[]>([]) as Ref<T[]>;
        const collectionCount = ref<number>(0);
        const collectionTotal = ref<number>(0);
        const collectionLoading = ref<boolean>(false);
        const collectionError = ref<any>(null);

        const load = async (updatedParams?: { [key: string]: any }) => {
            collectionParams.value = updatedParams ?? collectionParams.value;
            const { data, total } = await client.getSubResources(parent, Class, subResourceIdentifier);

            try {
                collectionError.value = null;
                collectionLoading.value = true;
                collectionData.value.splice(0);
                collectionData.value = data;
                collectionCount.value = data.length;
                collectionTotal.value = total;
                collectionLoading.value = false;
            } catch (error) {
                collectionError.value = error;
            } finally {
                collectionLoading.value = false;
            }
        };

        return {
            data: collectionData,
            count: collectionCount,
            total: collectionTotal,
            loading: collectionLoading,
            error: collectionError,
            load,
        };
    };

    const getReactiveSubCollection = async <T extends Resource>(parent: Resource, Class: new (...args: any[]) => T, subResourceIdentifier: string, params: object = {}): Promise<ReactiveCollection<T>> => {
        const collection = getLazySubCollection<T>(parent, Class, subResourceIdentifier, params);
        await collection.load();

        return collection;
    };

    return {
        client,
        currentUser: ref(client.session.currentUser) as Ref<User>,
        currentCompany: ref(client.session.currentCompany) as Ref<Company>,
        currentLegacyUser: legacyUser as Ref<LegacyUser> ?? ref(null),
        currentLegacyCompany: legacyCompany as Ref<LegacyCompany> ?? ref(null),
        getLazyReactiveCollection,
        getReactiveCollection,
        getLazySubCollection,
        getReactiveSubCollection,
    };
}
